import { useMemo } from 'react';
import { useAuth } from 'lib/Auth';
import { getInvitesV2 } from 'requests/invite';
import { BusinessUnitUser } from 'data/BusinessUnitUser';
import { hasOfmpAdminProfile, hasVendorAdminProfileForVendor } from 'data/User';
import { useUserProfiles } from './useUserProfiles';

interface UseBusinessUnitUsersProps {
    businessUnitId?: string;
    reload?: boolean;
    setReload?: (reload: boolean) => void;
    vendorId?: number;
    businessUnitsIds?: string;
}

/**
 * Hook for managing business unit users
 * Uses the generic useUserProfiles hook with business-unit-specific configuration
 */
export const useBusinessUnitUsers = ({
    businessUnitId,
    reload,
    setReload,
    vendorId,
    businessUnitsIds
}: UseBusinessUnitUsersProps) => {

    const { user } = useAuth();

    // Determine businessUnitIdParam based on user role
    const businessUnitIdParam = useMemo(() => {
        // Check if user is OFMP Admin or VendorAdmin for the selected vendor
        const isOfmpAdmin = hasOfmpAdminProfile(user);
        const isVendorAdminForVendor = hasVendorAdminProfileForVendor(user, vendorId);

        if (isOfmpAdmin || isVendorAdminForVendor) {
            // OFMP Admin or VendorAdmin for this vendor
            if (businessUnitId) {
                // Specific business unit selected - filter by that BU
                return businessUnitId;
            } else {
                // "All" business units selected - fetch ALL users (no BU filter)
                return undefined;
            }
        } else {
            // BU-level users - use existing logic
            return businessUnitId || businessUnitsIds;
        }
    }, [user, businessUnitId, businessUnitsIds, vendorId]);

    return useUserProfiles<BusinessUnitUser>({
        fetchFunction: getInvitesV2,
        buildParams: ({ currentPage, rowsPerPage, sortBy, sortOrder, searchTerm }) => [
            businessUnitIdParam,
            currentPage + 1, // API expects 1-based page
            rowsPerPage,
            sortBy,
            sortOrder,
            vendorId,
            searchTerm
        ],
        reload,
        setReload,
        successMessages: {
            delete: 'Profile deleted successfully',
            bulkDelete: 'Profile(s) deleted successfully'
        }
    });
};